package com.arena_web.ui.components.booking

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.*
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.components.events.PopupSlideUp
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*

private const val TECH_SERVICE_FEE = 2.50

@Composable
fun BookCourtPopup(
    isOpen: Boolean,
    clubId: String,
    onClose: () -> Unit,
    onBooked: () -> Unit = {}
) {
    if (!isOpen) return

    val container = LocalAppContainer.current
    val sportColors = LocalSportColors.current
    val scope = rememberCoroutineScope()

    // Data state
    var clubDetail by remember { mutableStateOf<PublicClubDetailDto?>(null) }
    var schedule by remember { mutableStateOf<ClubScheduleDto?>(null) }
    var isLoadingData by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    // Selection state
    var selectedFieldId by remember { mutableStateOf<String?>(null) }
    var selectedDate by remember {
        val now = kotlin.js.Date()
        mutableStateOf(formatJsDate(now))
    }
    var duration by remember { mutableStateOf(1.0) }
    var availability by remember { mutableStateOf<FieldAvailabilityDto?>(null) }
    var pricing by remember { mutableStateOf<List<FieldPricingDto>>(emptyList()) }
    var selectedTimeSlot by remember { mutableStateOf<TimeSlotAvailabilityDto?>(null) }
    var selectedServices by remember { mutableStateOf<Set<String>>(emptySet()) }
    var isSubmitting by remember { mutableStateOf(false) }
    var isLoadingAvailability by remember { mutableStateOf(false) }

    // Load club detail + schedule
    LaunchedEffect(clubId) {
        isLoadingData = true
        errorMessage = null
        try {
            val detailResult = container.clubPublicRepository.getClubDetail(clubId)
            val scheduleResult = container.clubPublicRepository.getSchedule(clubId)
            detailResult.onSuccess { detail ->
                clubDetail = detail
                if (detail.fields.isNotEmpty() && selectedFieldId == null) {
                    selectedFieldId = detail.fields.first().id
                }
            }.onFailure { errorMessage = "Failed to load club details" }
            scheduleResult.onSuccess { schedule = it }
        } catch (e: Exception) {
            errorMessage = "Failed to load club details"
        }
        isLoadingData = false
    }

    // Load pricing when field changes
    LaunchedEffect(selectedFieldId) {
        val fieldId = selectedFieldId ?: return@LaunchedEffect
        container.clubPublicRepository.getFieldPricing(clubId, fieldId)
            .onSuccess { pricing = it }
    }

    // Load availability when field or date changes
    LaunchedEffect(selectedFieldId, selectedDate) {
        val fieldId = selectedFieldId ?: return@LaunchedEffect
        isLoadingAvailability = true
        selectedTimeSlot = null
        container.clubPublicRepository.getFieldAvailability(
            clubId, fieldId, mapOf("date" to selectedDate)
        ).onSuccess { availability = it }
            .onFailure { availability = null }
        isLoadingAvailability = false
    }

    // Compute available time slots based on duration
    // Backend returns 1-hour slots, so:
    // 1h = 1 slot, 1.5h = 2 slots, 2h = 2 slots
    val slotsNeededForDuration = when {
        duration <= 1.0 -> 1
        duration <= 1.5 -> 2
        else -> 2
    }
    val availableSlots = remember(availability, duration, slotsNeededForDuration) {
        val slots = availability?.slots ?: return@remember emptyList()
        if (slotsNeededForDuration <= 1) {
            slots
        } else {
            slots.mapIndexed { index, slot ->
                if (!slot.isAvailable) return@mapIndexed slot
                val needed = (index until (index + slotsNeededForDuration))
                val allAvailable = needed.all { i -> i < slots.size && slots[i].isAvailable }
                if (allAvailable) slot
                else TimeSlotAvailabilityDto(startTime = slot.startTime, endTime = slot.endTime, isAvailable = false)
            }
        }
    }

    // Compute end time based on selected slot + duration (arithmetic)
    val computedEndTime = remember(selectedTimeSlot, duration) {
        val slot = selectedTimeSlot ?: return@remember null
        addHoursToTime(slot.startTime, duration)
    }

    // Compute price
    val courtFee = remember(pricing, duration) {
        val pricePerHour = pricing.firstOrNull()?.pricePerHour?.toDoubleOrNull() ?: 0.0
        pricePerHour * duration
    }
    val servicesTotalFee = remember(selectedServices, clubDetail) {
        val services = clubDetail?.services ?: return@remember 0.0
        selectedServices.sumOf { serviceId ->
            services.find { it.id == serviceId }?.price?.toDoubleOrNull() ?: 0.0
        }
    }
    val totalPrice = courtFee + servicesTotalFee + TECH_SERVICE_FEE

    // Date strip
    var dateStripOffset by remember { mutableStateOf(0) }
    val visibleDates = remember(dateStripOffset) {
        (0 until 10).map { offset ->
            val d = kotlin.js.Date()
            d.asDynamic().setDate(d.getDate() + dateStripOffset + offset)
            d
        }
    }

    // Overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier {
                property("background-color", "rgba(0, 0, 0, 0.55)")
                property("backdrop-filter", "blur(8px)")
            }
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Popup card
        Column(
            modifier = Modifier
                .sBorderRadius(24)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("width", "90vw")
                    property("height", "85vh")
                    property("max-width", "1400px")
                    property("border", "0.5px solid rgba(46, 96, 217, 0.25)")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(6, 14, 30, 0.97) 0%, rgba(3, 7, 18, 0.98) 100%)"
                    )
                    property("box-shadow", "0 32px 80px rgba(0, 0, 0, 0.6)")
                    property("overflow", "hidden")
                }
        ) {
            // ── Header ──
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(28, 36)
                    .sPaddingBottom(20),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Column(modifier = Modifier.sGap(6)) {
                    SpanText(
                        "book your court",
                        modifier = Modifier
                            .sFontSize(32)
                            .fontWeight(FontWeight.SemiBold)
                            .color(AppColors.PrimaryWhite)
                            .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                    )
                    SpanText(
                        "Select your preferred date, court, and time",
                        modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted)
                    )
                }
                Box(
                    modifier = Modifier
                        .sSize(40)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick { onClose() }
                        .styleModifier {
                            property("background", "rgba(255, 255, 255, 0.06)")
                            property("border", "1px solid rgba(255, 255, 255, 0.08)")
                            property("flex-shrink", "0")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(src = AppIcons.Close, modifier = Modifier.sSize(18))
                }
            }

            // ── Body ──
            if (isLoadingData) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText("Loading...", modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary))
                }
            } else if (errorMessage != null) {
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(errorMessage ?: "", modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary))
                }
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sPadding(0, 36)
                        .sPaddingBottom(28)
                        .sGap(16)
                ) {
                    // ── Left: Court List ──
                    CourtListColumn(
                        fields = clubDetail?.fields ?: emptyList(),
                        selectedFieldId = selectedFieldId,
                        onFieldSelected = {
                            selectedFieldId = it
                            selectedTimeSlot = null
                        },
                        modifier = Modifier.fillMaxHeight().styleModifier {
                            property("flex", "0 0 auto")
                            property("width", s(230))
                        }
                    )

                    // ── Center: Date + Duration + Time ──
                    CenterColumn(
                        visibleDates = visibleDates,
                        selectedDate = selectedDate,
                        onDateSelected = {
                            selectedDate = it
                            selectedTimeSlot = null
                        },
                        onPrevWeek = { dateStripOffset = (dateStripOffset - 7).coerceAtLeast(0) },
                        onNextWeek = { dateStripOffset += 7 },
                        duration = duration,
                        onDurationSelected = {
                            duration = it
                            selectedTimeSlot = null
                        },
                        slots = availableSlots,
                        selectedTimeSlot = selectedTimeSlot,
                        onTimeSlotSelected = { selectedTimeSlot = it },
                        isLoadingAvailability = isLoadingAvailability,
                        modifier = Modifier.fillMaxHeight().styleModifier { property("flex", "1 1 0px") }
                    )

                    // ── Right: Services + Payment ──
                    RightColumn(
                        services = clubDetail?.services ?: emptyList(),
                        selectedServices = selectedServices,
                        onToggleService = { serviceId ->
                            selectedServices = if (serviceId in selectedServices)
                                selectedServices - serviceId
                            else
                                selectedServices + serviceId
                        },
                        courtFee = courtFee,
                        techFee = TECH_SERVICE_FEE,
                        totalPrice = totalPrice,
                        duration = duration,
                        canConfirm = selectedTimeSlot != null && !isSubmitting,
                        isSubmitting = isSubmitting,
                        onConfirm = {
                            val slot = selectedTimeSlot ?: return@RightColumn
                            val endTime = computedEndTime ?: slot.endTime
                            val bookingRepo = container.bookingRepository
                            if (bookingRepo == null) {
                                errorMessage = "Please sign in to book a court"
                                return@RightColumn
                            }
                            isSubmitting = true
                            AnalyticsTracker.trackClick(
                                "book_court",
                                screen = "club_details",
                                params = mapOf("club_id" to clubId),
                            )
                            scope.launch {
                                val request = CreateBookingRequest(
                                    clubId = clubId,
                                    fieldId = selectedFieldId!!,
                                    bookingDate = selectedDate,
                                    startTime = slot.startTime,
                                    endTime = endTime,
                                    services = if (selectedServices.isNotEmpty())
                                        selectedServices.map { BookingServiceEntry(serviceId = it) }
                                    else null
                                )
                                bookingRepo.createBooking(request)
                                    .onSuccess { onBooked() }
                                    .onFailure { errorMessage = it.message ?: "Booking failed" }
                                isSubmitting = false
                            }
                        },
                        modifier = Modifier.fillMaxHeight().styleModifier {
                            property("flex", "0 0 auto")
                            property("width", s(280))
                        }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Court List (left panel)
// =============================================================================

@Composable
private fun CourtListColumn(
    fields: List<FieldDto>,
    selectedFieldId: String?,
    onFieldSelected: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val sportColors = LocalSportColors.current
    Column(
        modifier = modifier
            .sBorderRadius(16)
            .sPadding(16)
            .sGap(4)
            .overflow(Overflow.Auto)
            .styleModifier {
                property("min-width", "0")
                property("border", "0.5px solid rgba(46, 96, 217, 0.2)")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.06) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.1) transparent")
            }
    ) {
        // Header
        SpanText(
            "court",
            modifier = Modifier
                .bodyXlMedium()
                .color(AppColors.PrimaryWhite)
                .sPaddingBottom(12)
        )

        if (fields.isEmpty()) {
            SpanText("No courts available", modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted))
        } else {
            fields.forEach { field ->
                val isSelected = field.id == selectedFieldId
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(10)
                        .sPadding(10, 12)
                        .cursor(Cursor.Pointer)
                        .onClick { onFieldSelected(field.id) }
                        .styleModifier {
                            if (isSelected) {
                                property("border-left", "3px solid ${sportColors.primary}")
                                property("background", "rgba(0, 97, 255, 0.10)")
                            } else {
                                property("border-left", "3px solid transparent")
                            }
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Sport icon
                    Box(
                        modifier = Modifier
                            .sSize(24)
                            .sBorderRadius(6)
                            .styleModifier {
                                property("flex-shrink", "0")
                                property("background", "rgba(46, 96, 217, 0.15)")
                                property("margin-right", s(10))
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            src = AppIcons.Location,
                            modifier = Modifier.sSize(14).styleModifier {
                                property("filter", AppColors.IconDefaultFilter)
                                property("opacity", "0.6")
                            }
                        )
                    }
                    SpanText(
                        field.name,
                        modifier = Modifier
                            .bodySMedium()
                            .color(if (isSelected) AppColors.PrimaryWhite else AppColors.Text.Secondary)
                            .styleModifier {
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Center Column: Date Strip + Duration + Time Slots
// =============================================================================

@Composable
private fun CenterColumn(
    visibleDates: List<kotlin.js.Date>,
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    onPrevWeek: () -> Unit,
    onNextWeek: () -> Unit,
    duration: Double,
    onDurationSelected: (Double) -> Unit,
    slots: List<TimeSlotAvailabilityDto>,
    selectedTimeSlot: TimeSlotAvailabilityDto?,
    onTimeSlotSelected: (TimeSlotAvailabilityDto) -> Unit,
    isLoadingAvailability: Boolean,
    modifier: Modifier = Modifier
) {
    val sportColors = LocalSportColors.current

    Column(
        modifier = modifier
            .sGap(0)
            .styleModifier { property("min-width", "0") }
    ) {
        // ── Date Strip (no panel bg — sits at top) ──
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .sPaddingBottom(16),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Prev arrow
            Box(
                modifier = Modifier
                    .sSize(28)
                    .cursor(Cursor.Pointer)
                    .onClick { onPrevWeek() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    "<",
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Bold)
                        .color(sportColors.primary)
                )
            }

            // Date items
            Row(
                modifier = Modifier
                    .styleModifier { property("flex", "1") }
                    .sGap(0),
                horizontalArrangement = Arrangement.SpaceEvenly,
                verticalAlignment = Alignment.Bottom
            ) {
                visibleDates.forEach { date ->
                    val dateStr = formatJsDate(date)
                    val isSelected = dateStr == selectedDate
                    val dayName = getDayName(date).uppercase().take(3)
                    val dayNum = date.getDate().toInt()
                    val today = kotlin.js.Date()
                    val isToday = formatJsDate(today) == dateStr

                    Column(
                        modifier = Modifier
                            .cursor(Cursor.Pointer)
                            .onClick { onDateSelected(dateStr) }
                            .sPadding(4, 6),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Day label
                        SpanText(
                            dayName,
                            modifier = Modifier
                                .sFontSize(11)
                                .fontWeight(FontWeight.Normal)
                                .color(AppColors.Text.Muted)
                                .styleModifier { property("letter-spacing", "0.5px") }
                        )
                        // Day number
                        SpanText(
                            "$dayNum",
                            modifier = Modifier
                                .sFontSize(if (isSelected) 22 else 18)
                                .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                                .color(if (isSelected) AppColors.PrimaryWhite else AppColors.Text.Secondary)
                                .sPaddingTop(2)
                        )
                        // Month label on today's date
                        if (isToday) {
                            SpanText(
                                getMonthName(date).uppercase().take(3),
                                modifier = Modifier
                                    .sFontSize(10)
                                    .fontWeight(FontWeight.Medium)
                                    .color(AppColors.Text.Muted)
                            )
                        }
                    }
                }
            }

            // Next arrow
            Box(
                modifier = Modifier
                    .sSize(28)
                    .cursor(Cursor.Pointer)
                    .onClick { onNextWeek() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    ">",
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Bold)
                        .color(sportColors.primary)
                )
            }
        }

        // ── Duration + Time slots panel ──
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .sBorderRadius(16)
                .sPadding(20)
                .sGap(20)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("border", "0.5px solid rgba(46, 96, 217, 0.2)")
                    property(
                        "background",
                        "linear-gradient(172deg, rgba(60, 121, 241, 0.06) 14%, rgba(5, 4, 12, 0.03) 69%)"
                    )
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.1) transparent")
                }
        ) {
            // Duration
            Column(modifier = Modifier.sGap(10)) {
                SpanText(
                    "DURATION",
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Muted)
                        .styleModifier { property("letter-spacing", "1.5px") }
                )
                Row(modifier = Modifier.sGap(10)) {
                    listOf(1.0 to "1 hour", 1.5 to "1.5 hours", 2.0 to "2 hours").forEach { (dur, label) ->
                        val isActive = duration == dur
                        Box(
                            modifier = Modifier
                                .sHeight(38)
                                .sBorderRadius(8)
                                .cursor(Cursor.Pointer)
                                .onClick { onDurationSelected(dur) }
                                .styleModifier {
                                    property("padding", "0 ${s(20)}")
                                    if (isActive) {
                                        property("background", sportColors.primary.toString())
                                    } else {
                                        property("border", "1px solid rgba(46, 96, 217, 0.3)")
                                        property("background", "transparent")
                                    }
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                label,
                                modifier = Modifier
                                    .bodyLMedium()
                                    .color(if (isActive) sportColors.onPrimary else AppColors.Text.Secondary)
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }

            // Time Slots
            Column(modifier = Modifier.sGap(10).fillMaxWidth()) {
                SpanText(
                    "SELECT TIME",
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Muted)
                        .styleModifier { property("letter-spacing", "1.5px") }
                )

                if (isLoadingAvailability) {
                    Box(modifier = Modifier.fillMaxWidth().sHeight(80), contentAlignment = Alignment.Center) {
                        SpanText("Loading...", modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted))
                    }
                } else if (slots.isEmpty()) {
                    Box(modifier = Modifier.fillMaxWidth().sHeight(80), contentAlignment = Alignment.Center) {
                        SpanText("No time slots available", modifier = Modifier.bodyLRegular().color(AppColors.Text.Muted))
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth().sGap(8)) {
                        slots.chunked(3).forEach { row ->
                            Row(modifier = Modifier.fillMaxWidth().sGap(8)) {
                                row.forEach { slot ->
                                    val isSelected = selectedTimeSlot?.startTime == slot.startTime
                                    // Compute the end time based on duration (arithmetic)
                                    val displayEndTime = addHoursToTime(slot.startTime, duration)

                                    Box(
                                        modifier = Modifier
                                            .sHeight(38)
                                            .sBorderRadius(8)
                                            .cursor(if (slot.isAvailable) Cursor.Pointer else Cursor.Default)
                                            .onClick { if (slot.isAvailable) onTimeSlotSelected(slot) }
                                            .styleModifier {
                                                property("flex", "1 1 0px")
                                                if (isSelected) {
                                                    property("border", "1.5px solid ${sportColors.primary}")
                                                    property("background", "rgba(0, 97, 255, 0.10)")
                                                } else if (slot.isAvailable) {
                                                    property("border", "1px solid rgba(46, 96, 217, 0.25)")
                                                    property("background", "transparent")
                                                } else {
                                                    property("border", "1px solid rgba(255, 255, 255, 0.04)")
                                                    property("background", "transparent")
                                                    property("opacity", "0.35")
                                                }
                                            },
                                        contentAlignment = Alignment.Center
                                    ) {
                                        SpanText(
                                            "${slot.startTime.take(5)} \u2013 ${displayEndTime.take(5)}",
                                            modifier = Modifier
                                                .bodySMedium()
                                                .color(
                                                    when {
                                                        isSelected -> AppColors.PrimaryWhite
                                                        slot.isAvailable -> AppColors.Text.Secondary
                                                        else -> AppColors.Text.Muted
                                                    }
                                                )
                                        )
                                    }
                                }
                                repeat(3 - row.size) {
                                    Box(modifier = Modifier.styleModifier { property("flex", "1 1 0px") })
                                }
                            }
                        }
                    }
                }
            }

            // Spacer
            Box(modifier = Modifier.flexGrow(1))

            // Timezone note
            Row(
                modifier = Modifier.sGap(6),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.InfoCircle,
                    modifier = Modifier.sSize(14).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                        property("opacity", "0.5")
                    }
                )
                SpanText(
                    "All times are shown in your local timezone",
                    modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                )
            }
        }
    }
}

// =============================================================================
// Right Column: Services + Payment + CTA
// =============================================================================

@Composable
private fun RightColumn(
    services: List<ClubServiceDto>,
    selectedServices: Set<String>,
    onToggleService: (String) -> Unit,
    courtFee: Double,
    techFee: Double,
    totalPrice: Double,
    duration: Double,
    canConfirm: Boolean,
    isSubmitting: Boolean,
    onConfirm: () -> Unit,
    modifier: Modifier = Modifier
) {
    val sportColors = LocalSportColors.current

    Column(
        modifier = modifier
            .sBorderRadius(16)
            .sPadding(18)
            .sGap(16)
            .styleModifier {
                property("min-width", "0")
                property("border", "0.5px solid rgba(46, 96, 217, 0.2)")
                property(
                    "background",
                    "linear-gradient(172deg, rgba(60, 121, 241, 0.06) 14%, rgba(5, 4, 12, 0.03) 69%)"
                )
                property("overflow-y", "auto")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.1) transparent")
            }
    ) {
        // ── Services section ──
        Column(modifier = Modifier.sGap(4)) {
            SpanText(
                "enhance your session",
                modifier = Modifier
                    .bodyXlMedium()
                    .color(AppColors.PrimaryWhite)
            )
            SpanText(
                "Add services to make your game even better",
                modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
            )
        }

        if (services.isNotEmpty()) {
            Column(modifier = Modifier.sGap(8)) {
                services.forEach { service ->
                    val isSelected = service.id in selectedServices
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(12)
                            .sPadding(12)
                            .cursor(Cursor.Pointer)
                            .onClick { onToggleService(service.id) }
                            .styleModifier {
                                property("border", "0.5px solid rgba(46, 96, 217, 0.2)")
                                property(
                                    "background",
                                    "linear-gradient(172deg, rgba(60, 121, 241, 0.04) 14%, rgba(5, 4, 12, 0.02) 69%)"
                                )
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Service icon circle
                        Box(
                            modifier = Modifier
                                .sSize(36)
                                .sBorderRadius(100)
                                .styleModifier {
                                    property("flex-shrink", "0")
                                    property("background", "rgba(46, 96, 217, 0.12)")
                                    property("border", "0.5px solid rgba(46, 96, 217, 0.2)")
                                    property("margin-right", s(10))
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                src = AppIcons.Location,
                                modifier = Modifier.sSize(16).styleModifier {
                                    property("filter", AppColors.IconDefaultFilter)
                                    property("opacity", "0.6")
                                }
                            )
                        }

                        // Name + description
                        Column(
                            modifier = Modifier.sGap(1).styleModifier {
                                property("flex", "1")
                                property("min-width", "0")
                            }
                        ) {
                            SpanText(
                                service.name,
                                modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                            )
                            if (!service.description.isNullOrBlank()) {
                                SpanText(
                                    service.description,
                                    modifier = Modifier
                                        .sFontSize(11)
                                        .color(AppColors.Text.Muted)
                                        .styleModifier {
                                            property("white-space", "nowrap")
                                            property("overflow", "hidden")
                                            property("text-overflow", "ellipsis")
                                        }
                                )
                            }
                        }

                        // Price
                        SpanText(
                            "+$${formatPrice(service.price.toDoubleOrNull() ?: 0.0)}",
                            modifier = Modifier
                                .bodySMedium()
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("margin-right", s(8))
                                }
                        )

                        // Toggle circle
                        Box(
                            modifier = Modifier
                                .sSize(24)
                                .sBorderRadius(100)
                                .styleModifier {
                                    property("flex-shrink", "0")
                                    if (isSelected) {
                                        property("background", sportColors.primary.toString())
                                    } else {
                                        property("background", "transparent")
                                        property("border", "2px solid rgba(255, 255, 255, 0.15)")
                                    }
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            if (isSelected) {
                                SpanText(
                                    "\u2713",
                                    modifier = Modifier
                                        .sFontSize(13)
                                        .fontWeight(FontWeight.Bold)
                                        .color(sportColors.onPrimary)
                                )
                            }
                        }
                    }
                }
            }
        }

        // ── Spacer ──
        Box(modifier = Modifier.flexGrow(1))

        // ── Payment Summary ──
        Column(modifier = Modifier.sGap(8)) {
            SpanText(
                "PAYMENT SUMMARY",
                modifier = Modifier
                    .sFontSize(12)
                    .fontWeight(FontWeight.SemiBold)
                    .color(AppColors.Text.Muted)
                    .styleModifier { property("letter-spacing", "1.5px") }
            )

            // Court fee
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                SpanText(
                    "Court Fee (${formatDuration(duration)})",
                    modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                )
                SpanText(
                    "$${formatPrice(courtFee)}",
                    modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                )
            }

            // Selected service lines
            services.filter { it.id in selectedServices }.forEach { service ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    SpanText(
                        service.name,
                        modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                    )
                    SpanText(
                        "$${formatPrice(service.price.toDoubleOrNull() ?: 0.0)}",
                        modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                    )
                }
            }

            // Tech fee
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                SpanText(
                    "Tech Service Fee",
                    modifier = Modifier.bodySRegular().color(AppColors.Text.Muted)
                )
                SpanText(
                    "$${formatPrice(techFee)}",
                    modifier = Modifier.bodySMedium().color(AppColors.PrimaryWhite)
                )
            }
        }

        // ── Confirm Booking CTA ──
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(48)
                .sBorderRadius(12)
                .cursor(if (canConfirm) Cursor.Pointer else Cursor.Default)
                .onClick { if (canConfirm) onConfirm() }
                .styleModifier {
                    property("background", sportColors.primary.toString())
                    property("flex-shrink", "0")
                    if (!canConfirm) property("opacity", "0.5")
                },
            contentAlignment = Alignment.Center
        ) {
            Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    if (isSubmitting) "Booking..." else "Confirm Booking",
                    modifier = Modifier
                        .bodyLMedium()
                        .fontWeight(FontWeight.SemiBold)
                        .color(sportColors.onPrimary)
                )
                if (!isSubmitting) {
                    SpanText(
                        "\u2192",
                        modifier = Modifier
                            .sFontSize(18)
                            .color(sportColors.onPrimary)
                    )
                }
            }
        }
    }
}

// =============================================================================
// Utility functions
// =============================================================================

private fun formatJsDate(date: kotlin.js.Date): String {
    val y = date.getFullYear()
    val m = (date.getMonth() + 1).toInt().toString().padStart(2, '0')
    val d = date.getDate().toInt().toString().padStart(2, '0')
    return "$y-$m-$d"
}

private fun getDayName(date: kotlin.js.Date): String {
    val names = arrayOf("Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday")
    return names[date.getDay()]
}

private fun getMonthName(date: kotlin.js.Date): String {
    val names = arrayOf("January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December")
    return names[date.getMonth()]
}

private fun formatDuration(duration: Double): String = when (duration) {
    1.0 -> "1h"
    1.5 -> "1.5h"
    2.0 -> "2h"
    else -> "${duration}h"
}

private fun formatPrice(amount: Double): String {
    return amount.asDynamic().toFixed(2) as String
}

/** Add [durationHours] (e.g. 1.0, 1.5, 2.0) to a "HH:mm" time string and return "HH:mm". */
private fun addHoursToTime(startTime: String, durationHours: Double): String {
    val parts = startTime.take(5).split(":")
    val h = parts[0].toIntOrNull() ?: return startTime
    val m = parts.getOrNull(1)?.toIntOrNull() ?: 0
    val totalMinutes = h * 60 + m + (durationHours * 60).toInt()
    val endH = (totalMinutes / 60) % 24
    val endM = totalMinutes % 60
    return "${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}"
}
